import React, {FC, useState, useEffect, useMemo, useRef} from 'react'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useAuth} from '../../../auth'
import {getVesselList} from '../../../Management/core/_requests'
import {getRunningHourCounters, deleteRunningHourCounter} from '../../core/pms/_requests'
import {getEquipmentByVessel} from '../../core/_requests'
import {RunningHourCounterDto} from '../../core/pms/_models'
import {EquipmentDto} from '../../core/_models'
import {toast, ToastContainer} from 'react-toastify'
import {RunningHoursModal} from './RunningHoursModal'
import {RunningHoursHistoryModal} from './RunningHoursHistoryModal'
import {EditCounterModal} from './EditCounterModal'
import {CreateCounterModal} from './CreateCounterModal'
import type {Vessel} from '../../../Management/core/_models'
import {getAccessibleVessels, isVesselAssignedUserOnly} from '../../core/technicalScopeUtils'
import {TECH_PERM} from '../../core/technicalPermissions'
import {TechnicalAccessDenied} from '../TechnicalAccessDenied'
import SearchableSelect from '../../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../../shared/context/GlobalFilterContext'

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

const RunningHoursPage: FC = () => {
  const {currentUser, isSuperAdmin, hasPermission} = useAuth()
  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)
  const canReadPms = hasPermission(TECH_PERM.READ_RUNNING_HOURS)
  const canCreatePms = hasPermission(TECH_PERM.CREATE_RUNNING_HOURS)
  const canUpdatePms = hasPermission(TECH_PERM.UPDATE_RUNNING_HOURS)
  const canDeletePms = hasPermission(TECH_PERM.DELETE_RUNNING_HOURS)
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  const [filters, setFilters] = useState<{
    companyId: string
    subcompanyId: string
    vesselId: string
    equipmentId: string
  }>({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
    equipmentId: '',
  })

  // Sync global header vessel filter → local vesselId
  useEffect(() => {
    const gvId = globalState.vessel?.id
    if (gvId != null) {
      setFilters((prev) => ({ ...prev, vesselId: String(gvId), equipmentId: '' }))
    } else if (isVesselOnlyUser && currentUser?.vessel?.id) {
      setFilters((prev) => ({ ...prev, vesselId: String(currentUser.vessel.id), equipmentId: '' }))
    } else {
      setFilters((prev) => ({ ...prev, vesselId: '', equipmentId: '' }))
    }
  }, [globalState.vessel, isVesselOnlyUser, currentUser?.vessel?.id])

  const [counters, setCounters] = useState<RunningHourCounterDto[]>([])
  const [loading, setLoading] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [subcompaniesForChosenCompany, setSubcompaniesForChosenCompany] = useState<Subcompany[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])
  const [equipment, setEquipment] = useState<EquipmentDto[]>([])
  const [loadingLookups, setLoadingLookups] = useState(false)
  const filtersDisabled = loading || loadingLookups

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedCounter, setSelectedCounter] = useState<RunningHourCounterDto | null>(null)
  const [editCounterOpen, setEditCounterOpen] = useState(false)
  const [editingCounter, setEditingCounter] = useState<RunningHourCounterDto | null>(null)
  const [createCounterOpen, setCreateCounterOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyCounter, setHistoryCounter] = useState<RunningHourCounterDto | null>(null)
  const [deletingCounterId, setDeletingCounterId] = useState<number | null>(null)
  const countersRequestId = useRef(0)
  const equipmentRequestId = useRef(0)
  const currentVesselIdRef = useRef<string>('')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  // Sorting
  const [sortColumn, setSortColumn] = useState<keyof RunningHourCounterDto | ''>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  useEffect(() => {
    loadLookups()
  }, [])

  useEffect(() => {
    if (filters.vesselId) {
      loadEquipment(Number(filters.vesselId))
    } else {
      setEquipment([])
    }
  }, [filters.vesselId])

  useEffect(() => {
    loadCounters()
  }, [filters.vesselId, filters.equipmentId])

  useEffect(() => {
    currentVesselIdRef.current = filters.vesselId
  }, [filters.vesselId])

  const loadLookups = async () => {
    setLoadingLookups(true)
    try {
      const [groupsRaw, adminsRaw, vesselsData] = await Promise.all([
        import('../../../Management/core/_requests').then((m) =>
          m.getCompanyAdminList?.().catch(() => [])
        ),
        import('../../../Management/core/_requests').then((m) =>
          m.getCompanyList?.().catch(() => [])
        ),
        getVesselList().catch(() => [] as Vessel[]),
      ])

      const groups: CompanyGroup[] = Array.isArray(groupsRaw)
        ? groupsRaw.map((g: any) => ({
            id: Number(g.id),
            name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
          }))
        : []

      const admins: Subcompany[] = Array.isArray(adminsRaw)
        ? adminsRaw.map((a: any) => ({
            id: Number(a.id),
            name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
            companyId: Number(
              a.cgaid?.id ?? a.cga?.id ?? a.companyGroupAdminId ?? a.companyGroupId
            ),
          }))
        : []

      const validGroups = groups.filter((g) => Number.isFinite(g.id))
      const validAdmins = admins.filter(
        (a) => Number.isFinite(a.id) && Number.isFinite(a.companyId)
      )
      setCompanies(validGroups)
      setSubcompanies(validAdmins)

      const vesselsForUser = getAccessibleVessels(vesselsData, currentUser, isSuperAdmin)
      setVesselsScoped(vesselsForUser)
    } catch (error) {
      console.error('Error loading lookups:', error)
    } finally {
      setLoadingLookups(false)
    }
  }

  const loadEquipment = async (vesselId: number) => {
    const requestId = ++equipmentRequestId.current
    try {
      const data = await getEquipmentByVessel(vesselId)
      if (requestId !== equipmentRequestId.current) return
      if (filters.vesselId && Number(filters.vesselId) !== vesselId) return
      setEquipment(data)
    } catch (error) {
      console.error('Error loading equipment:', error)
    }
  }

  const loadCounters = async () => {
    setLoading(true)
    const requestId = ++countersRequestId.current
    try {
      const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined
      const data = await getRunningHourCounters(vesselId)
      if (requestId !== countersRequestId.current) return
      setCounters(data)
    } catch (error) {
      console.error('Error loading counters:', error)
      toast.error('Failed to load running hour counters')
    } finally {
      if (requestId === countersRequestId.current) {
        setLoading(false)
      }
    }
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setFilters((prev) => {
      const next = {...prev, [name]: value}
      if (name === 'companyId') {
        next.subcompanyId = ''
        next.vesselId = ''
        next.equipmentId = ''
      }
      if (name === 'subcompanyId') {
        next.vesselId = ''
        next.equipmentId = ''
      }
      if (name === 'vesselId') {
        next.equipmentId = ''
      }
      return next
    })
  }

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      equipmentId: '',
    }))
    setSearchTerm('')
  }

  const subcompaniesForChosenCompanyMemo = useMemo(() => {
    if (!filters.companyId) return []
    const cid = Number(filters.companyId)
    return subcompanies.filter((sc) => sc.companyId === cid)
  }, [filters.companyId, subcompanies])

  useEffect(() => {
    setSubcompaniesForChosenCompany(subcompaniesForChosenCompanyMemo)
  }, [subcompaniesForChosenCompanyMemo])

  const allowedVesselIds = useMemo(() => {
    if (!globalState.company) return null
    if (!globalVessels || globalVessels.length === 0) return null
    return new Set(globalVessels.map((v: any) => Number(v.id)))
  }, [globalState.company, globalVessels])

  const vesselsForFilters = useMemo(() => {
    let list = vesselsScoped
    if (allowedVesselIds != null) {
      list = list.filter((v: any) => allowedVesselIds.has(Number(v.id)))
    }
    return list
  }, [vesselsScoped, allowedVesselIds])

  const filteredData = useMemo(() => {
    let data = [...counters]

    // If company is selected in global filter and "All Vessels" is active, filter by company vessels
    if (!filters.vesselId && allowedVesselIds != null) {
      data = data.filter((c) => c.vesselId && allowedVesselIds.has(Number(c.vesselId)))
    }

    // Filter by equipment
    if (filters.equipmentId) {
      const eqId = Number(filters.equipmentId)
      data = data.filter((c) => c.equipmentId === eqId)
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      data = data.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          (c.equipmentName && c.equipmentName.toLowerCase().includes(term)) ||
          (c.vesselName && c.vesselName.toLowerCase().includes(term))
      )
    }

    // Sort
    if (sortColumn) {
      data.sort((a, b) => {
        const aVal = a[sortColumn]
        const bVal = b[sortColumn]
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal
        }

        const aStr = String(aVal).toLowerCase()
        const bStr = String(bVal).toLowerCase()
        if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1
        if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1
        return 0
      })
    }

    return data
  }, [counters, filters.vesselId, allowedVesselIds, filters.equipmentId, searchTerm, sortColumn, sortOrder])

  const handleSort = (column: keyof RunningHourCounterDto) => {
    setSortColumn((prevCol) => {
      if (prevCol === column) {
        setSortOrder((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prevCol
      }
      setSortOrder('asc')
      return column
    })
  }

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(filteredData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handleAddReading = (counter: RunningHourCounterDto) => {
    if (!canCreatePms) {
      toast.error('You do not have permission to add readings.', {position: 'top-center'})
      return
    }
    setSelectedCounter(counter)
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setSelectedCounter(null)
  }

  const handleModalSuccess = () => {
    handleModalClose()
    loadCounters()
  }

  const handleEditCounter = (counter: RunningHourCounterDto) => {
    if (!canUpdatePms) {
      toast.error('You do not have permission to edit counters.', {position: 'top-center'})
      return
    }
    setEditingCounter(counter)
    setEditCounterOpen(true)
  }

  const handleEditCounterSuccess = () => {
    setEditCounterOpen(false)
    setEditingCounter(null)
    loadCounters()
  }

  const handleOpenHistory = (counter: RunningHourCounterDto) => {
    setHistoryCounter(counter)
    setHistoryOpen(true)
  }

  const handleDeleteRunningHourCounter = async (counter: RunningHourCounterDto) => {
    if (!canDeletePms) {
      toast.error('You do not have permission to delete counters.', {position: 'top-center'})
      return
    }
    const counterId = counter.id
    if (counterId == null) {
      toast.error('Invalid counter')
      return
    }
    const label = counter.name || `Counter #${counterId}`
    const ok = window.confirm(
      `Permanently delete "${label}"?\n\nThis will remove all reading history/logs for this counter and delete the counter. ` +
        `Linked PMS plan lines will be detached from this counter. This cannot be undone.`
    )
    if (!ok) return
    setDeletingCounterId(counterId)
    try {
      await deleteRunningHourCounter(counterId)
      toast.success('Running hour counter and all readings were deleted.')
      if (historyCounter?.id === counterId) {
        setHistoryOpen(false)
        setHistoryCounter(null)
      }
      if (selectedCounter?.id === counterId) {
        setModalOpen(false)
        setSelectedCounter(null)
      }
      if (editingCounter?.id === counterId) {
        setEditCounterOpen(false)
        setEditingCounter(null)
      }
      await loadCounters()
    } catch (e: any) {
      toast.error('Failed to delete running hour counter')
    } finally {
      setDeletingCounterId(null)
    }
  }

  if (!canReadPms) {
    return (
      <TechnicalAccessDenied
        title='Running hours'
        message='You do not have permission to view running hours (READ_RUNNING_HOURS).'
      />
    )
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Running Hours</h3>
                <span className='text-muted fs-7'>
                  Manage running hour counters and readings for equipment
                </span>
              </div>
              <div className='card-toolbar d-flex align-items-center'>
                {canCreatePms && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary me-2 btn-outline'
                    onClick={() => setCreateCounterOpen(true)}
                    disabled={filtersDisabled || !filters.vesselId}
                    title={!filters.vesselId ? 'Please select a vessel in the header to add a new counter' : undefined}
                  >
                    <KTSVG
                      path='/media/icons/duotune/general/gen035.svg'
                      className='svg-icon-3 me-1'
                    />
                    Add New Counter
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>

                {/* Equipment */}
                {filters.vesselId && (
                  <div className='col-md-2'>
                    <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                      Equipment
                    </label>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      name='equipmentId'
                      value={filters.equipmentId}
                      onChange={handleFilterChange}
                      disabled={filtersDisabled}
                    >
                      <option value=''>All Equipment</option>
                      {equipment.map((eq) => (
                        <option key={eq.id} value={String(eq.id)}>
                          {eq.name} {eq.code && `(${eq.code})`}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                {/* Search */}
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Search
                  </label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{top: '50%', transform: 'translateY(-50%)', zIndex: 1}}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Counter name, equipment...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={filtersDisabled}
                    />
                  </div>
                </div>

                <div className='col-md-2 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={handleClearFilters}
                    disabled={filtersDisabled}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className='report-table table-responsive' style={{position: 'relative'}}>
                {loading && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(255,255,255,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5,
                    }}
                  >
                    <div className='spinner-border' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                )}
                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th style={{minWidth: '70px'}} className='text-center text-nowrap'>
                          Sr/No
                        </th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Counter Name</span>
                            <button
                              type='button'
                              className='btn btn-sm btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('name')}
                              disabled={counters.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'name'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '180px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Vessel</span>
                          </div>
                        </th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Equipment</span>
                          </div>
                        </th>
                        <th style={{minWidth: '150px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Last Recorded Value</span>
                            <button
                              type='button'
                              className='btn btn-sm btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('currentValue')}
                              disabled={counters.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'currentValue'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '180px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Last Reading Date</span>
                            <button
                              type='button'
                              className='btn btn-sm btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('lastReadingDate')}
                              disabled={counters.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'lastUpdatedAt'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '150px', textAlign: 'center'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='table-body text-start'>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className='text-center py-5'>
                            <div className='spinner-border' role='status'>
                              <span className='visually-hidden'>Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : currentRecords.length === 0 ? (
                        <tr>
                          <td colSpan={7} className='text-center py-5 text-muted'>
                            No running hour counters found
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((counter, idx) => (
                          <tr key={counter.id}>
                            <td className='text-center'>{indexOfFirstRecord + idx + 1}</td>
                            <td className='fw-semibold'>{counter.name}</td>
                            <td>{counter.vesselName || '-'}</td>
                            <td>{counter.equipmentName || '-'}</td>
                            <td className='text-end'>{counter.currentValue.toLocaleString()}</td>
                            <td>
                              {counter.lastReadingDate
                                ? new Date(counter.lastReadingDate).toLocaleDateString()
                                : '-'}
                            </td>
                            <td style={{textAlign: 'center'}}>
                              <div className='d-flex justify-content-center gap-1'>
                                {canCreatePms && (
                                  <button
                                    className='btn btn-sm btn-primary text-nowrap'
                                    onClick={() => handleAddReading(counter)}
                                  >
                                    Add Reading
                                  </button>
                                )}
                                {canUpdatePms && (
                                  <button
                                    className='btn btn-sm btn-light'
                                    onClick={() => handleEditCounter(counter)}
                                    title='Edit Counter'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen055.svg'
                                      className='svg-icon-3'
                                    />
                                  </button>
                                )}
                                <button
                                  className='btn btn-sm btn-light'
                                  onClick={() => handleOpenHistory(counter)}
                                  title='Reading History'
                                >
                                  <i className='bi bi-clock-history fs-5' />
                                </button>
                                {canDeletePms && (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light text-danger'
                                    onClick={() => handleDeleteRunningHourCounter(counter)}
                                    disabled={deletingCounterId === counter.id}
                                    title='Delete counter and all reading history'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen027.svg'
                                      className='svg-icon-3'
                                    />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {!loading && filteredData.length > 0 && (
                  <div
                    className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                    style={{
                      position: 'static',
                      bottom: 0,
                      backgroundColor: '#fff',
                      zIndex: 10,
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-2'>Rows per page</span>
                      <SearchableSelect
                        className='form-select'
                        style={{
                          borderRadius: '20px',
                          width: '70px',
                          border: '1px solid #dee2e6',
                          fontSize: '14px',
                          padding: '4px 8px',
                        }}
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </SearchableSelect>
                    </div>

                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-3' style={{fontSize: '14px'}}>
                        Showing{' '}
                        <strong>
                          {indexOfFirstRecord + 1}-
                          {Math.min(indexOfLastRecord, filteredData.length)}
                        </strong>{' '}
                        of <strong>{filteredData.length}</strong>
                      </span>

                      <nav>
                        <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px',
                              }}
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              ‹
                            </button>
                          </li>

                          {(() => {
                            const pages = []
                            const showPages = 5
                            let startPage = Math.max(1, currentPage - 2)
                            let endPage = Math.min(totalPages, startPage + showPages - 1)

                            if (endPage - startPage < showPages - 1) {
                              startPage = Math.max(1, endPage - showPages + 1)
                            }

                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li
                                  key={i}
                                  className={`page-item ${currentPage === i ? 'active' : ''}`}
                                >
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor:
                                        currentPage === i ? '#F4F9FF' : 'transparent',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px',
                                    }}
                                    onClick={() => handlePageChange(i)}
                                  >
                                    {i}
                                  </button>
                                </li>
                              )
                            }

                            return pages
                          })()}

                          <li
                            className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                          >
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px',
                              }}
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              ›
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {canCreatePms && modalOpen && selectedCounter && (
        <RunningHoursModal
          visible={modalOpen}
          onClose={handleModalClose}
          counter={selectedCounter}
          onSuccess={handleModalSuccess}
        />
      )}

      {canUpdatePms && editCounterOpen && editingCounter && (
        <EditCounterModal
          visible={editCounterOpen}
          onClose={() => {
            setEditCounterOpen(false)
            setEditingCounter(null)
          }}
          counter={editingCounter}
          onSuccess={handleEditCounterSuccess}
        />
      )}

      {canCreatePms && (
        <CreateCounterModal
          visible={createCounterOpen}
          onClose={() => setCreateCounterOpen(false)}
          vesselId={filters.vesselId ? Number(filters.vesselId) : undefined}
          vesselName={
            filters.vesselId
              ? (vesselsForFilters.find((v) => String(v.id) === filters.vesselId) as any)
                  ?.fleet_name ||
                vesselsForFilters.find((v) => String(v.id) === filters.vesselId)?.name
              : undefined
          }
          equipmentOptions={equipment.filter(
            (eq) => !counters.some((c) => c.equipmentId === eq.id)
          )}
          onSuccess={(createdCounter) => {
            setCounters((prev) => {
              if (!createdCounter?.id) return prev
              if (
                currentVesselIdRef.current &&
                String(createdCounter.vesselId) !== currentVesselIdRef.current
              ) {
                return prev
              }
              if (prev.some((counter) => counter.id === createdCounter.id)) {
                return prev.map((counter) =>
                  counter.id === createdCounter.id ? createdCounter : counter
                )
              }
              return [createdCounter, ...prev]
            })
            loadCounters()
            if (filters.vesselId) {
              loadEquipment(Number(filters.vesselId))
            }
          }}
        />
      )}

      {historyOpen && historyCounter && (
        <RunningHoursHistoryModal
          visible={historyOpen}
          counter={historyCounter}
          onClose={() => {
            setHistoryOpen(false)
            setHistoryCounter(null)
          }}
          onUpdated={() => {
            loadCounters()
          }}
        />
      )}

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{top: '5rem'}}
      />
    </div>
  )
}

export default RunningHoursPage
